import type { Provider } from '@app/src/types/organizations'
import { ProviderLinkStatus } from '@app/src/types/organizations'
import type { AssessmentTemplate, OrganizationAssessmentSettings } from '@app/src/types/resourceExplorer'
import { AssessmentType } from '@app/src/types/resourceExplorer'
import { isDateInFuture } from '@app/src/utils/helpersTs'
import type { BulkCreateExpectationFormData, BulkCreateExpectationSubmissionPayload } from './types'
import { BulkCreateExpectationFormField } from './types'

export const buildDefaultFormValues = (initialProviderIds: number[]): BulkCreateExpectationFormData => ({
  [BulkCreateExpectationFormField.AssessmentTemplateId]: null,
  [BulkCreateExpectationFormField.TargetAssessmentLevel]: 4,
  [BulkCreateExpectationFormField.ProviderIds]: [...initialProviderIds],
  [BulkCreateExpectationFormField.Deadline]: null,
  [BulkCreateExpectationFormField.ImportantKpis]: [],
})

const isActivated = (assessmentSettings: OrganizationAssessmentSettings[], templateId: number): boolean =>
  assessmentSettings.some(setting => setting.assessmentTemplate.id === templateId && setting.isActivated)

/** Baseline is always allowed, as before; other assessments only behind the flag and when activated. */
export const canSetExpectationForTemplate = (
  assessmentTemplate: Pick<AssessmentTemplate, 'id' | 'assessmentType'> | undefined,
  assessmentSettings: OrganizationAssessmentSettings[],
  isMultiAssessmentEnabled: boolean,
): boolean => {
  if (!assessmentTemplate) return false
  if (assessmentTemplate.assessmentType === AssessmentType.BaselineAssessment) return true
  return isMultiAssessmentEnabled && isActivated(assessmentSettings, assessmentTemplate.id)
}

/**
 * Baseline comes from its own query so the flow keeps working exactly as before when multiple
 * assessments are not enabled, whether or not Baseline is activated in the assessment settings.
 * A preselected assessment is the only choice: its page passes providers with only that
 * assessment's results, so any other assessment would find nobody eligible.
 */
export const getExpectationAssessmentTemplates = (
  baselineTemplate: AssessmentTemplate | undefined,
  assessmentSettings: OrganizationAssessmentSettings[],
  preselectedTemplate: AssessmentTemplate | undefined,
  isMultiAssessmentEnabled: boolean,
): AssessmentTemplate[] => {
  const baseline = baselineTemplate ? [baselineTemplate] : []
  if (!isMultiAssessmentEnabled) return baseline

  if (preselectedTemplate) {
    return canSetExpectationForTemplate(preselectedTemplate, assessmentSettings, isMultiAssessmentEnabled)
      ? [preselectedTemplate]
      : []
  }

  const activated = assessmentSettings
    .filter(setting => setting.isActivated)
    .map(setting => setting.assessmentTemplate)
    .filter(template => template.id !== baselineTemplate?.id)

  return [...baseline, ...activated]
}

export const pickInitialAssessmentTemplateId = (
  templates: AssessmentTemplate[],
  preselectedTemplateId: number | undefined,
): number | null => templates.find(t => t.id === preselectedTemplateId)?.id ?? templates[0]?.id ?? null

/** Only the top level for templates with three levels or fewer; otherwise the top two, lowest first. */
export const getSelectableTargetLevels = (levels: number | undefined): number[] => {
  const topLevel = levels ?? 5
  return topLevel > 3 ? [topLevel - 1, topLevel] : [topLevel]
}

export const getDefaultTargetLevel = (levels: number | undefined): number => getSelectableTargetLevels(levels)[0]

export const isFutureDate = (value: string | null): boolean => {
  if (!value) return false
  return isDateInFuture(value)
}

export const canSubmit = (formData: BulkCreateExpectationFormData): boolean => {
  const templateId = formData[BulkCreateExpectationFormField.AssessmentTemplateId]
  const providerIds = formData[BulkCreateExpectationFormField.ProviderIds]
  const deadline = formData[BulkCreateExpectationFormField.Deadline]
  return templateId != null && providerIds.length > 0 && isFutureDate(deadline)
}

export const buildSubmissionPayload = (
  formData: BulkCreateExpectationFormData,
): BulkCreateExpectationSubmissionPayload => {
  const templateId = formData[BulkCreateExpectationFormField.AssessmentTemplateId]
  const deadline = formData[BulkCreateExpectationFormField.Deadline]
  if (templateId == null) throw new Error('buildSubmissionPayload: assessmentTemplateId is null')
  if (deadline == null) throw new Error('buildSubmissionPayload: deadline is null')
  const importantKpis = formData[BulkCreateExpectationFormField.ImportantKpis]
  return {
    assessmentTemplateId: templateId,
    targetAssessmentLevel: formData[BulkCreateExpectationFormField.TargetAssessmentLevel],
    providerIds: formData[BulkCreateExpectationFormField.ProviderIds],
    deadline,
    ...(importantKpis.length > 0 ? { importantQuestions: importantKpis } : {}),
  }
}

export type ProviderBuckets = {
  belowTarget: Provider[]
  notConnected: Provider[]
  missingSelfReported: Provider[]
  alreadyCovered: Provider[]
  alreadyOnTarget: Provider[]
}

export const findBaselineLevel = (provider: Provider, templateId: number | null): number | undefined => {
  if (!templateId) return undefined
  const matching = provider.assessments?.filter(a => a.assessmentTemplateId === templateId && a.totalLevel != null)
  if (!matching || matching.length === 0) return undefined
  const latest = [...matching].sort((a, b) => (b.periodName ?? '').localeCompare(a.periodName ?? ''))[0]
  if (latest.isScreened) return undefined
  return latest.totalLevel
}

export const bucketProviders = (
  providers: Provider[],
  templateId: number | null,
  targetLevel: number,
  coveredProviderIds: ReadonlySet<number>,
): ProviderBuckets => {
  const belowTarget: Provider[] = []
  const notConnected: Provider[] = []
  const missingSelfReported: Provider[] = []
  const alreadyCovered: Provider[] = []
  const alreadyOnTarget: Provider[] = []

  for (const provider of providers) {
    if (provider.linkStatus !== ProviderLinkStatus.Connected) {
      notConnected.push(provider)
      continue
    }

    if (coveredProviderIds.has(provider.id)) {
      alreadyCovered.push(provider)
      continue
    }

    const level = findBaselineLevel(provider, templateId)

    if (level == null) {
      missingSelfReported.push(provider)
    } else if (level >= targetLevel) {
      alreadyOnTarget.push(provider)
    } else {
      belowTarget.push(provider)
    }
  }

  return { belowTarget, notConnected, missingSelfReported, alreadyCovered, alreadyOnTarget }
}

export const getEligibleSelection = (
  selectedIds: number[],
  providers: Provider[],
  templateId: number | null,
  targetLevel: number,
  coveredProviderIds: ReadonlySet<number>,
): number[] => {
  const eligible = new Set(
    bucketProviders(providers, templateId, targetLevel, coveredProviderIds).belowTarget.map(p => p.id),
  )
  return selectedIds.filter(id => eligible.has(id))
}
